import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { TreeNode } from 'primeng/api';

import { Utils } from '../../../../common/utils';
import { GenerateFormDefinition } from './form.definition';
import { FormType } from '../../../../common/components/reactive-form-builder/interfaces/form.interface';

import { FincasApiService } from '../../../../service/api/fincas-api.service';
import { SiexApiService } from '../../../../service/api/siex-api.service';
import { build, stack } from '../../../../common/classes/request-builder';

import { FincasModel } from '../../../../models/form-common/fincas.module';
import { VisitasVariedadMesModel } from '../../../../models/visitas_variedad_mes/visitas_variedad_mes.model';

import { GenerateEnvironmentDefinition } from '../environment-variables';

/** ——————————————————— Constants (no magic strings) ——————————————————— */
const REPORT_ENDPOINT_PATH = 'ws/informes/index.php' as const;
const PARAM_INFORME_TRATAMIENTOS = 'informe_tratamientos' as const;
const PARAM_LIBRO_FERTILIZANTES  = 'libro_fertilizantes'  as const;

@Component({
  selector: 'app-generate-modal',
  standalone: false,
  templateUrl: './generate-modal.component.html',
  styleUrls: ['./generate-modal.component.scss']
})
export class GenerateModalComponent<T> implements OnInit, OnDestroy {
  /* ——— Inputs & Outputs ——— */  
  @Input() public model!: T | TreeNode<FincasModel>;

  @Output() public hideDialog = new EventEmitter<void>();

  /* ——— Form wrapper ——— */
  public form!: GenerateFormDefinition;

  /** Environment flags for feature toggles and labels */
  public readonly env: GenerateEnvironmentDefinition = new GenerateEnvironmentDefinition();

  /** Emits when dropdowns are ready to (re)load */
  public readonly dropdownLoadTrigger = new ReplaySubject<void>(1);

  constructor(
    private readonly fincasApi: FincasApiService,
    private readonly siexApi: SiexApiService
  ) {void fincasApi;}

  /* ————————————————————— Lifecycle ————————————————————— */

  ngOnInit(): void {
    // Build the form structure bound to the current node’s data
    this.form = new GenerateFormDefinition(+FormType.GENERATION, (this.model as TreeNode<FincasModel>).data ?? ({} as FincasModel), null);

    // Load lists into the form (in parallel)
    stack([
      build(this.fincasApi.sectoresGET,  this.form.sectores,  'nombre',   true),
      build(this.siexApi.variedadesClienteGET, this.form.variedades, 
              (it: VisitasVariedadMesModel) => `${it.cultivo ?? ''}${it.variedad ? ` (${it.variedad})` : ''}`, true),
    ])
      .then(() => { this.dropdownLoadTrigger.next(); })
      .catch((e) => { console.log('Generate lists load error:', e); });
  }

  ngOnDestroy(): void {
    this.dropdownLoadTrigger.complete();
  }

  /* ————————————————————— Public API ————————————————————— */

  /**
   * Compose and open the Cuaderno report URL.
   * Supports optional Treatment and Fertilizer sub-reports.
   */
  public generarCuaderno(informe_tratamientos = false, libro_fertilizantes = false): void {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { fecha_inicio, fecha_fin } = this.form.model;

    console.log(this.form.model);
    if (fecha_inicio == null || fecha_fin == null) {
      alert('Debe especificar un intervalo de fechas.');
      return;
    }

    const fincaData = this.safeFincaData(this.model as TreeNode<FincasModel>);
    const idVariedad = this.selectedVariedadId(this.env.appName);

    const { fechaInicio, fechaFin, anyo } = this.getDates(fecha_inicio, fecha_fin);

    const params = new URLSearchParams({
      finca:        String(fincaData.nombre ?? ''),
      id_finca:     String(fincaData.id ?? ''),
      id_sector:    String(this.form?.sectores.selected?.id ?? ''),
      id_cliente:   String(fincaData.id_cliente ?? ''),
      app_name:     this.env.appName,
      id_variedad:  String(idVariedad ?? ''),
      id_user:      String(this.env.user?.id ?? ''),
      fecha_fin:    String(fechaFin),
      fecha_inicio: String(fechaInicio),
      anyo:         String(anyo),
    });

    if (informe_tratamientos) { params.set(PARAM_INFORME_TRATAMIENTOS, 'true'); }
    if (libro_fertilizantes)  { params.set(PARAM_LIBRO_FERTILIZANTES,  'true'); }

    const base = (Boolean(this.env.serverUrl.endsWith('/')))
      ? this.env.serverUrl
      : `${this.env.serverUrl}/`;

    window.open(`${base}${REPORT_ENDPOINT_PATH}?${params.toString()}`, '_blank');
  }

  /** Normalize two dates and compute either a single year or a year range. */
  public getDates(
    inicio: string | Date,
    fin: string | Date
  ): { fechaInicio: string; fechaFin: string; anyo: string } {
    const fechaInicio = inicio instanceof Date ? Utils.formatDate(inicio) : inicio;
    const fechaFin    = fin    instanceof Date ? Utils.formatDate(fin)    : fin;

    const yIni = fechaInicio.split('/')[2] ?? '';
    const yFin = fechaFin.split('/')[2] ?? yIni;
    const anyo = yIni === yFin ? yIni : `${yIni}-${yFin}`;

    return { fechaInicio, fechaFin, anyo };
  }

  /** Form builder → keep the live model in sync. */
  public formChanges(formModel: object, _tag: string): void {
    this.form.model = formModel as FincasModel;
  }

  /* ————————————————————— Helpers ————————————————————— */

  private safeFincaData(node: TreeNode<FincasModel> | null | undefined): Partial<FincasModel> {
    return (node?.data ?? {}) as Partial<FincasModel>;
  }

  /** Resolve selected variedad id(s) according to app name rules. */
  private selectedVariedadId(appName: string): string {
    if (appName === 'covidai') {
      // Multi-select: join selected ids with commas
      return (this.form.variedades.selected as VisitasVariedadMesModel[] | null | undefined)
        ?.map(v => v?.id)
        .filter(Boolean)
        .join(',') ?? '';
    }

    // Single-select: the API returns a VisitasVariedadMesModel; use its `id`
    const sel = this.form.variedades.selected as VisitasVariedadMesModel | null;
    
    return sel?.id?.toString() ?? '';
  }
}
